/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Button from '@narvar/nth-button';
import TextArea from '@narvar/nth-text-area';
import PhotoUploader from '../PhotoUploader';
import ReturnsSidebar from '../../react-components/ReturnsSidebar/ReturnsSidebar';
import SidebarSectionTitle from '../../react-components/SidebarSectionTitle/SidebarSectionTitle';
import ItemInfo from '../../react-components/SidebarItemDetails/SidebarItemDetails';
import ButtonMenu from '../../react-components/ButtonMenu';
import styles from './returnReasonSidebar.module.css';
import getPrevSelectedItemCount from '../../lib/getPrevSelectedItemCount';
import QuantityField from '../QuantityField';
import { handlePhotoUpload } from '../../lib/reasons';
import { DEFAULT_MAX_COMMENTS_COUNT } from '../../constants/defaultSettings';
import OverrideSidebar from './components/OverrideSidebar';
import ProductOptionsMenu from '../ProductOptionsMenu';
import { RECEIVE_CLAIMS_SIDEBAR_TOGGLE } from '../../actions/claims';
import { ActionTypes } from '../../constants/enums';
import { Spinner } from '../../components/Spinner';

export const mapStateToProps = ({
  claims: { enabled: claimsEnabled } = {},
  language,
  page: { languageCode, productType, deferOverride } = {},
  photoUpload: { isUploading, images } = {},
  restrictions: { itemLimit, maxItemsReached },
  returnsSettings: {
    [productType]: {
      sidebar_item_attrs: sidebarItemAttrs,
      sidebar_comments: {
        [productType]: {
          comments_count: maxCommentsCount,
          comments_count_disabled: commentsCountDisabled,
        } = {},
      } = {},
    } = {},
  } = {},
  itemsPage: { isFetchingAddCartItem, isFetchingRemoveCartItem } = {},
}) => ({
  language,
  languageCode,
  maxCommentsCount,
  itemLimit,
  maxItemsReached,
  commentsCountDisabled,
  sidebarItemAttrs,
  isUploading,
  images,
  deferOverride,
  claimsEnabled,
  isFetchingAddCartItem,
  isFetchingRemoveCartItem,
});

const ReturnReasonSidebar = (props) => {
  const {
    dispatch,
    language,
    languageCode,
    maxCommentsCount,
    itemLimit,
    maxItemsReached,
    commentsCountDisabled,
    sidebarState,
    sidebarItemAttrs,
    isUploading,
    images,
    handlers,
    deferOverride,
    claimsEnabled,
    isFetchingAddCartItem,
    isFetchingRemoveCartItem,
  } = props;

  const {
    reasonCodeSelected,
    showQuantityFields,
    commentBoxTitle,
    handleSelectProduct,
    actions: {
      toggle,
      revertReasons,
      selectReason,
      updateComment,
      saveItem,
      deleteItem,
      selectQuantity,
      handleOnQtyChange,
      handleExpiryGroupChange,
      handleVerifyQtyField,
      handleAddExpiryGroup,
      removeGroup,
    },
  } = handlers;

  const {
    currentItem,
    returnReasonCodes,
    nestedReasons,
    selectedReason,
    selectedNestedReason,
    savedItems,
    open,
    returnQuantity,
    expiryList,
    eligibleQtyList,
    returnComment,
    disableSidebarComments,
    nestedReasonsTitle,
    isExchange,
    exchangeEligibility,
    commentHidden,
    commentRequired,
    commentPlaceholder,
    quantityField,
    quantityFieldAction,
    isVerifying,
    error,
    photoUploadEnabled,
    photoUploadRequired,
    photoUploadPlaceholder,
    photoUploadTitle,
    photoUploadMaxFiles,
  } = sidebarState;
  const [returnCommentValue, updateCommentValue] = React.useState(returnComment);

  React.useEffect(() => {
    updateCommentValue(returnComment);
  }, [currentItem]);

  const isFetching = isUploading || isFetchingAddCartItem || isFetchingRemoveCartItem;
  const nestedUnselected = nestedReasons && !selectedNestedReason;
  const commentEmpty = commentRequired && !returnCommentValue;
  const missingQtyInput = quantityField && returnQuantity < 1;
  const missingValidation =
    quantityField && quantityField.validate && expiryList.some(({ valid }) => !valid);
  const disableSubmit =
    !selectedReason ||
    nestedUnselected ||
    commentEmpty ||
    isUploading ||
    missingQtyInput ||
    missingValidation ||
    (photoUploadRequired && images.length < 1);

  const currentItemId = currentItem && currentItem.item_id;
  const itemSaved = savedItems[currentItemId];
  const showCommentBox = (!commentHidden || commentRequired) && !disableSidebarComments;
  const maxLengthClassName =
    returnCommentValue.length === maxCommentsCount
      ? `${styles.sidebar_comments_max_length} sidebar-comments-max-length`
      : '';
  const prevSelectedItemCount = getPrevSelectedItemCount(savedItems, currentItemId);
  const isLastItemBeforeLimit = itemLimit - 1 === prevSelectedItemCount;
  const itemLimitDisclaimer = language['returns.step_1.sidepanel_item_limit_disclaimer'];

  if (deferOverride && currentItem?.disabledOverride) {
    return (
      <OverrideSidebar
        {...{
          open,
          toggle,
          currentItem,
          sidebarItemAttrs,
        }}
      />
    );
  }

  return (
    <div role="dialog" tabIndex="0" aria-label="Return Reasons Sidebar" aria-modal={open}>
      <ReturnsSidebar
        title={language['returns.step_1.sidepanel_title']}
        toggleSide="right"
        sidebarOpen={open}
        closeIconOnClick={toggle}
        overlayOnClick={toggle}
        disableClose={maxItemsReached || isFetchingAddCartItem || isFetchingRemoveCartItem}
      >
        <div className={styles.panel_content}>
          <ItemInfo
            item={currentItem || {}}
            displayImg
            itemProperties={sidebarItemAttrs}
            language={language}
          />
          {isLastItemBeforeLimit && itemLimitDisclaimer && (
            <div className="item-limit-disclaimer">{itemLimitDisclaimer}</div>
          )}
          {isExchange &&
            !claimsEnabled &&
            (exchangeEligibility ? (
              <ProductOptionsMenu
                onClick={handleSelectProduct}
                disabled={Boolean(itemSaved)}
                options={[
                  {
                    type: ActionTypes.Return,
                    displayName: language['returns.step_1.sidepanel_product.return'],
                  },
                ]}
                checkSelected={(product) => product.type === ActionTypes.Return}
              />
            ) : (
              <div>{language['returns.step_1.return_eligibility_only_header']}</div>
            ))}
          {claimsEnabled && (
            <ProductOptionsMenu
              onClick={() => {
                toggle();
                dispatch({ type: RECEIVE_CLAIMS_SIDEBAR_TOGGLE, data: { sidebarOpen: true } });
              }}
              disabled={Boolean(itemSaved)}
              options={[
                {
                  type: ActionTypes.Return,
                  displayName: language['returns.step_1.item_sidepanel_product.return'],
                },
              ]}
              checkSelected={(product) => product.type === ActionTypes.Return}
            />
          )}
          <div className="reasons-container">
            <SidebarSectionTitle
              index={0}
              title={`1. ${language['returns.step_1.sidepanel_question']}`}
            />
            {selectedReason ? (
              <ButtonMenu
                menuItems={[selectedReason]}
                itemKey="reason_category"
                propName="reason_category"
                itemClick={isFetching ? () => {} : revertReasons}
                checkSelected={reasonCodeSelected}
              />
            ) : (
              <ButtonMenu
                menuItems={returnReasonCodes || []}
                itemKey="reason_category"
                propName="reason_category"
                itemClick={isFetching ? () => {} : selectReason}
                checkSelected={reasonCodeSelected}
              />
            )}
          </div>

          {nestedReasons ? (
            <div className="nested-reasons-container">
              <SidebarSectionTitle
                title={nestedReasonsTitle || language['returns.step_1.sidepanel_tell_us_more']}
              />
              <ButtonMenu
                menuItems={nestedReasons}
                itemKey="return_reason_code_id"
                propName="display_name"
                itemClick={isFetching ? () => {} : selectReason}
                checkSelected={reasonCodeSelected}
              />
            </div>
          ) : null}

          {quantityField && quantityField.type ? (
            <div>
              <SidebarSectionTitle
                title={`2. ${language['returns.step_1.sidepanel_return_quantity']}`}
                disabled={!showQuantityFields()}
              />
              {showQuantityFields() && (
                <QuantityField
                  {...quantityField}
                  handleOnQtyChange={handleOnQtyChange}
                  handleAddExpiryGroup={handleAddExpiryGroup}
                  handleExpiryGroupChange={handleExpiryGroupChange}
                  handleVerifyQtyField={handleVerifyQtyField}
                  expiryList={expiryList}
                  returnQuantity={returnQuantity}
                  quantityFieldAction={quantityFieldAction}
                  isVerifying={isVerifying}
                  removeGroup={removeGroup}
                  error={error}
                />
              )}
            </div>
          ) : eligibleQtyList && eligibleQtyList.length > 1 ? (
            <div>
              <SidebarSectionTitle
                title={`2. ${language['returns.step_1.sidepanel_return_quantity']}`}
                disabled={!showQuantityFields()}
              />
              {
                /* eslint-disable jsx-a11y/label-has-for */
                /* eslint-disable jsx-a11y/no-autofocus */
                showQuantityFields() ? (
                  <label htmlFor="returnQuantity" style={{ width: '100%' }}>
                    <select
                      id="returnQuantity"
                      name="returnQuantity"
                      className={`form-control ${styles.select_list} sidebar-return-qty`}
                      defaultValue={returnQuantity || 1}
                      onChange={selectQuantity}
                      aria-label="Return Item Quantity"
                      autoFocus
                    >
                      {eligibleQtyList.map((item) => (
                        <option key={item.toString()} value={item} className="narvar-btn-secondary">
                          {item}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null
              }
            </div>
          ) : null}

          {photoUploadEnabled && showQuantityFields() ? (
            <PhotoUploader
              title={photoUploadTitle}
              placeholder={photoUploadPlaceholder}
              onDrop={(acceptedFiles, rejectedFiles) =>
                handlePhotoUpload({
                  acceptedFiles,
                  rejectedFiles,
                  images,
                  photoUploadMaxFiles,
                  dispatch,
                  language,
                  languageCode,
                })
              }
            />
          ) : null}

          {showCommentBox ? (
            <div disabled={!showQuantityFields()}>
              <SidebarSectionTitle
                index={2}
                title={
                  commentRequired
                    ? commentBoxTitle(
                        language['returns.step_1.sidepanel_additional_comments_mandatory'],
                      )
                    : commentBoxTitle(language['returns.step_1.sidepanel_additional_comments'])
                }
                disabled={!showQuantityFields()}
                labelFor="returnComment"
              />

              {showQuantityFields() ? (
                <div>
                  {!commentsCountDisabled ? (
                    <p
                      className={`${styles.sidebar_comments_length} sidebar-comments-length ${maxLengthClassName}`}
                      aria-live="polite"
                      aria-atomic="true"
                      id="sidebar-comments-length"
                    >
                      {returnCommentValue.length}
                      <span className="sr-only">characters out of</span>
                      <span aria-hidden="true">/</span>
                      {maxCommentsCount}
                    </p>
                  ) : null}
                  <TextArea
                    id="returnComment"
                    className={`sidebar-comments ${styles.sidebar_comments} ${
                      returnCommentValue ? 'filled-input' : ''
                    }`}
                    style={{ width: '100%' }}
                    inputStyle={{ height: '5.5rem' }}
                    name="returnComment"
                    label={
                      commentPlaceholder ||
                      language['returns.step_1.sidepanel_comments_description']
                    }
                    labelStyle={{ top: '0.875rem', left: '0.875rem' }}
                    onChange={(e) => updateCommentValue(e.target.value)}
                    value={returnCommentValue}
                    tabIndex="0"
                    aria-label="Comment Box"
                    maxLength={maxCommentsCount}
                    aria-describedby="sidebar-comments-length"
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <Button
            className={`${styles.submit_button} narvar-btn-primary sidebar-done-button sidebar-action-btn`}
            disabled={disableSubmit || isFetchingAddCartItem || isFetchingRemoveCartItem}
            big
            onClick={() => {
              updateComment(returnCommentValue);
              saveItem();
            }}
          >
            {language['returns.step_1.sidepanel_next']}
            <div className={styles.button_spinner_container}>
              <span className="returns-spinner">
                {isFetchingAddCartItem && <Spinner width="30" />}
              </span>
            </div>
          </Button>

          {itemSaved && !disableSubmit && (
            <Button
              className={`${styles.sidebar_button} narvar-btn-tertiary sidebar-cancel-item-button sidebar-action-btn`}
              onClick={deleteItem}
              big
            >
              {language['returns.step_1.sidepanel_dont_return']}
              <div className={styles.button_spinner_container}>
                <span className="returns-spinner">
                  {isFetchingRemoveCartItem && <Spinner width="30" />}
                </span>
              </div>
            </Button>
          )}
        </div>
      </ReturnsSidebar>
    </div>
  );
};

ReturnReasonSidebar.defaultProps = {
  maxCommentsCount: DEFAULT_MAX_COMMENTS_COUNT,
  commentsCountDisabled: false,
  sidebarItemAttrs: ['Color', 'Size'],
  isFetchingAddCartItem: false,
  isFetchingRemoveCartItem: false,
};

ReturnReasonSidebar.propTypes = {
  dispatch: PropTypes.func.isRequired,
  language: PropTypes.object.isRequired,
  languageCode: PropTypes.string.isRequired,
  sidebarState: PropTypes.object.isRequired,
  handlers: PropTypes.object.isRequired,
  itemLimit: PropTypes.number.isRequired,
  maxItemsReached: PropTypes.bool.isRequired,
  maxCommentsCount: PropTypes.number,
  commentsCountDisabled: PropTypes.bool,
  sidebarItemAttrs: PropTypes.array,
  isUploading: PropTypes.bool.isRequired,
  images: PropTypes.array.isRequired,
  claimsEnabled: PropTypes.bool.isRequired,
  isFetchingAddCartItem: PropTypes.bool,
  isFetchingRemoveCartItem: PropTypes.bool,
};

export default connect(mapStateToProps)(ReturnReasonSidebar);
